import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Bot, Save, Loader2, ThumbsUp, ThumbsDown, LifeBuoy, ScrollText } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";

const isPrivilegedRole = (r) => ["super_admin", "sysadmin", "owner"].includes(r);
const inputCls = "w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition";
const labelCls = "block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1.5";
const PLAN_KEYS = ["free", "trial", "starter", "pro", "enterprise"];

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-line bg-bg-elevated px-4 py-3" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="text-xl font-black text-ink-primary mt-0.5">{value}</div>
    </div>
  );
}

export default function AdminAssistant() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings");

  const load = () => {
    api.get("/admin/assistant/overview").then((r) => { setData(r.data); setS(r.data.settings); }).catch(() => {});
    api.get("/admin/assistant/feedback").then((r) => setFeedback(r.data.feedback || [])).catch(() => {});
    api.get("/admin/assistant/escalations").then((r) => setEscalations(r.data.escalations || [])).catch(() => {});
    api.get("/admin/assistant/audit").then((r) => setAudit(r.data.audit || [])).catch(() => {});
  };
  useEffect(load, []);

  if (!user) return null;
  if (!isPrivilegedRole(user.role)) return <Navigate to="/dashboard" replace />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/assistant/settings", {
        enabled: s.enabled, maintenance: s.maintenance,
        provider: s.provider, model: s.model,
        welcome_message: s.welcome_message,
        suggested_prompts: s.suggested_prompts,
        rate_limit_per_hour: Number(s.rate_limit_per_hour) || 30,
        allowed_plans: s.allowed_plans || [],
        retention_days: Number(s.retention_days) || 0,
      });
      toast.success("Assistant settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const a = data?.analytics;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="admin-assistant-page">
      <PageHeader
        eyebrow="// ADMIN"
        title="BroadcastFlow Assistant"
        description="Configure Flow, review usage, feedback and support escalations."
      />

      {a && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Conversations (7d)" value={a.conversations_7d} testid="aa-stat-convs" />
          <Stat label="Active users (7d)" value={a.active_users_7d} testid="aa-stat-users" />
          <Stat label="👍 / 👎" value={`${a.feedback_up} / ${a.feedback_down}`} testid="aa-stat-feedback" />
          <Stat label="Escalations" value={a.escalations} testid="aa-stat-escalations" />
          <Stat label="Total conversations" value={a.total_conversations} testid="aa-stat-total" />
          <Stat label="Replies generated" value={a.assistant_messages} testid="aa-stat-msgs" />
          <Stat label="No-source answers" value={a.answers_without_kb_source} testid="aa-stat-nosource" />
          <Stat label="Est. tokens" value={a.estimated_tokens?.toLocaleString?.() || 0} testid="aa-stat-tokens" />
        </div>
      )}

      <div className="mb-6 flex items-center gap-1 border-b border-line">
        {[["settings", "Settings"], ["feedback", "Feedback"], ["escalations", "Escalations"], ["audit", "Audit log"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${tab === id ? "border-brand-amber text-brand-amber" : "border-transparent text-ink-secondary hover:text-ink-primary"}`}
                  data-testid={`aa-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "settings" && s && (
        <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5" data-testid="aa-settings-form">
          <div className="flex flex-wrap gap-8">
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="aa-enabled-switch" />
              Assistant enabled
            </label>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={!!s.maintenance} onCheckedChange={(v) => setS({ ...s, maintenance: v })} data-testid="aa-maintenance-switch" />
              Maintenance mode
            </label>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>AI provider</label>
              <select value={s.provider} onChange={(e) => setS({ ...s, provider: e.target.value })} className={inputCls} data-testid="aa-provider-select">
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="openai">OpenAI (GPT)</option>
                <option value="gemini">Google (Gemini)</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Model</label>
              <input value={s.model} onChange={(e) => setS({ ...s, model: e.target.value })} className={inputCls} data-testid="aa-model-input" />
            </div>
            <div>
              <label className={labelCls}>Messages per user / hour</label>
              <input type="number" min="1" value={s.rate_limit_per_hour} onChange={(e) => setS({ ...s, rate_limit_per_hour: e.target.value })} className={inputCls} data-testid="aa-rate-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Welcome message</label>
            <textarea value={s.welcome_message} onChange={(e) => setS({ ...s, welcome_message: e.target.value })} rows={3} className={inputCls} data-testid="aa-welcome-input" />
          </div>
          <div>
            <label className={labelCls}>Suggested prompts (one per line)</label>
            <textarea value={(s.suggested_prompts || []).join("\n")}
                      onChange={(e) => setS({ ...s, suggested_prompts: e.target.value.split("\n") })}
                      rows={6} className={inputCls} data-testid="aa-prompts-input" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Plan access (none checked = everyone)</label>
              <div className="flex flex-wrap gap-3">
                {PLAN_KEYS.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-xs text-ink-secondary capitalize">
                    <input type="checkbox" className="accent-brand-amber"
                           checked={(s.allowed_plans || []).includes(p)}
                           onChange={(e) => {
                             const set = new Set(s.allowed_plans || []);
                             e.target.checked ? set.add(p) : set.delete(p);
                             setS({ ...s, allowed_plans: [...set] });
                           }}
                           data-testid={`aa-plan-${p}`} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Conversation retention (days, 0 = keep forever)</label>
              <input type="number" min="0" value={s.retention_days} onChange={(e) => setS({ ...s, retention_days: e.target.value })} className={inputCls} data-testid="aa-retention-input" />
            </div>
          </div>
          <button onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 text-black font-semibold text-sm hover:bg-brand-amber/90 disabled:opacity-60 transition"
                  data-testid="aa-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </button>
        </div>
      )}

      {tab === "feedback" && (
        <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50" data-testid="aa-feedback-list">
          {feedback.length === 0 && <p className="p-6 text-sm text-ink-muted text-center">No feedback yet.</p>}
          {feedback.map((f, i) => (
            <div key={i} className="px-5 py-3 flex items-start gap-3 text-sm">
              {f.rating === "up" ? <ThumbsUp className="h-4 w-4 text-emerald-400 mt-0.5" /> : <ThumbsDown className="h-4 w-4 text-red-400 mt-0.5" />}
              <div className="min-w-0">
                <div className="text-ink-secondary text-xs">{(f.created_at || "").slice(0, 16)} · {f.module || "general"}</div>
                <div className="text-ink-primary truncate">{f.snippet}</div>
                {f.comment && <div className="text-ink-muted text-xs mt-0.5">"{f.comment}"</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "escalations" && (
        <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50" data-testid="aa-escalations-list">
          {escalations.length === 0 && <p className="p-6 text-sm text-ink-muted text-center">No escalations yet — Flow is handling it. 🎉</p>}
          {escalations.map((e, i) => (
            <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm">
              <LifeBuoy className="h-4 w-4 text-brand-amber" />
              <span className="font-mono text-brand-amber">{e.ticket_reference}</span>
              <span className="text-ink-muted text-xs">{(e.created_at || "").slice(0, 16)}</span>
              <span className="text-ink-secondary text-xs truncate">conversation {e.conversation_id}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "audit" && (
        <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50" data-testid="aa-audit-list">
          {audit.length === 0 && <p className="p-6 text-sm text-ink-muted text-center">No audit entries yet.</p>}
          {audit.map((row, i) => (
            <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm">
              <ScrollText className="h-4 w-4 text-ink-muted" />
              <span className="font-semibold text-ink-primary">{row.action}</span>
              <span className="text-ink-secondary text-xs truncate flex-1">{row.detail}</span>
              <span className="text-ink-muted text-xs">{(row.created_at || "").slice(0, 16)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
